// In Your Hands on one line: the badge and the date for a single SKU.
//
// <aps-when> is drawn by snippets/aps-when.liquid. This asks PIM once for the SKU
// through the delivery store in assets/aps-delivery.js, which the header loads on
// every page, and paints the answer the way the cart does: the tier from the
// store's one rule, the date in the shopper's own zone. It asks again when the
// header changes the zip and at the instant the answer's cutoff passes, so a page
// left open never shows a promise the clock has taken away.
//
// One function scope, because the header's script and the search's script are
// classic scripts too and a repeated top-level const stops a whole file.
(() => {
  // PIM returns UTC. Read in the shopper's zone or a late cutoff shows as the next day.
  const label = (iso) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  class ApsWhen extends HTMLElement {
    connectedCallback() {
      this.sku = this.dataset.sku;
      this.channel = this.dataset.channel || '16';
      this.badge = this.querySelector('[data-aps-tier]');
      this.date = this.querySelector('[data-aps-when-date]');
      if (!this.sku || !this.date) return;
      this.seq = 0;
      this.onZip = (e) => {
        if (e.detail && e.detail.zip) this.ask();
      };
      document.addEventListener('aps:delivery', this.onZip);
      this.ask();
    }

    disconnectedCallback() {
      document.removeEventListener('aps:delivery', this.onZip);
      clearTimeout(this.wake);
    }

    get store() {
      const s = window.apsDelivery;
      return s && typeof s.estimate === 'function' ? s : null;
    }

    ask() {
      const store = this.store;
      if (!store) {
        // The header's script has not run yet. Deferred scripts run in document order
        // and the header comes first, so this is a guard, not the usual path.
        if (!this.waited) {
          this.waited = true;
          window.addEventListener('load', () => this.ask(), { once: true });
        }
        return;
      }
      const seq = ++this.seq;
      store.estimate(this.sku, this.channel).then((data) => {
        if (seq !== this.seq) return;
        this.paint(data, store);
        clearTimeout(this.wake);
        const at = store.cutoffAt ? store.cutoffAt(data) : null;
        if (at) this.wake = setTimeout(() => this.ask(), at - Date.now() + 1500);
      });
    }

    paint(data, store) {
      if (this.badge) {
        const tier = store.tier ? store.tier(data) : null;
        if (tier) {
          const free = this.badge.dataset.free === 'true';
          this.badge.querySelector('[data-aps-tier-label]').textContent = (free ? 'FREE ' : '') + store.tierLabel(tier);
          this.badge.dataset.tier = tier;
          this.badge.hidden = false;
          // The host carries the tier too, so a stylesheet can drop the wording the
          // badge already says where the line is narrow.
          this.dataset.tier = tier;
        } else {
          this.badge.hidden = true;
          delete this.dataset.tier;
        }
      }
      // A SKU PIM does not know answers with no warehouse and a date it cannot stand
      // behind; a backordered one has no warehouse either but carries a day count and
      // a real date. Same reading as the cart.
      const routed = data && data.delivery_estimate && (data.warehouse_id || typeof data.backorder_handling === 'number');
      const text = routed ? label(data.delivery_estimate) : '';
      if (!text) {
        this.removeAttribute('data-ready');
        return;
      }
      this.date.textContent = text;
      this.setAttribute('data-ready', '');
    }
  }

  if (!customElements.get('aps-when')) customElements.define('aps-when', ApsWhen);
})();
